import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { forwardRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { faMegaphone } from "@fortawesome/pro-light-svg-icons"
import { faCircle } from "@fortawesome/pro-solid-svg-icons"
import { IconButton, SvgIcon, Tooltip, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalCache, StorageKey } from "../../../cache/LocalCache"
import { getGoPayNewsRoute, USER_REPORTS_GOPAY_NEWS_ROUTE } from "../../../../constants/routes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { fetchNewsCount } from "./newsBellApi"
import { useAppSelector } from "../../../../modules/store/storeHooks"
import { selectUserType } from "../../../../modules/login/loginSelectors"
import { UserType } from "../../../../modules/login/loginTypes"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    iconButton: {
        color: theme.palette.tailwind.neutral[500],
    },
    circleWrap: {
        display: "block",
        border: "1.5px solid #fff",
        color: theme.palette.tailwind.sky[500],
        position: "absolute",
        right: "0.5rem",
        top: "0.5rem",
        background: "white",
        borderRadius: "100%",
        flex: "none",
        width: "0.75rem",
        height: "0.75rem",
        lineHeight: "0.59rem",
    },
}))

type FontAwesomeSvgIconProps = {
    icon: any
}

// Hack to properly render FontAwesome icon inside MUI IconButton
// See: https://mui.com/material-ui/icons/#font-awesome
export const FontAwesomeSvgIcon = forwardRef<SVGSVGElement, FontAwesomeSvgIconProps>((props, ref) => {
    const { icon } = props

    const {
        icon: [width, height, , , svgPathData],
    } = icon

    return (
        <SvgIcon ref={ref} viewBox={`0 0 ${width} ${height}`}>
            {typeof svgPathData === "string" ? (
                <path d={svgPathData} />
            ) : (
                /**
                 * A multi-path Font Awesome icon seems to imply a duotune icon. The 0th path seems to
                 * be the faded element (referred to as the "secondary" path in the Font Awesome docs)
                 * of a duotone icon. 40% is the default opacity.
                 *
                 * @see https://fontawesome.com/how-to-use/on-the-web/styling/duotone-icons#changing-opacity
                 */
                svgPathData.map((d: string, i: number) => <path style={{ opacity: i === 0 ? 0.4 : 1 }} d={d} />)
            )}
        </SvgIcon>
    )
})

type Props = Readonly<{
    urlPrefix: string | undefined
    className?: string
}>

export function NewsBellNavItem({ urlPrefix, className }: Props) {
    const { classes, cx } = useStyles()
    const navigate = useNavigate()
    const actualUserType = useAppSelector(selectUserType)

    const [lastNewsCheckDateTime, setLastNewsCheckDateTime] = useState(() => {
        const storedTime = LocalCache().get(StorageKey.LAST_NEWS_CHECK)
        return storedTime ? DateTime.fromISO(storedTime) : null
    })

    const newsCountResult = useApiQuery(fetchNewsCount, {
        queryName: "news-count",
        dependencies: {},
        refetchInterval: 60000,
    })

    function handleClick() {
        const now = DateTime.now()
        LocalCache().set(StorageKey.LAST_NEWS_CHECK, now.toISO())
        setLastNewsCheckDateTime(now)
        if (actualUserType === UserType.USER) {
            navigate(USER_REPORTS_GOPAY_NEWS_ROUTE)
            return
        }
        navigate(getGoPayNewsRoute(urlPrefix))
    }
    const newsSinceDateTime = newsCountResult.data && DateTime.fromISO(newsCountResult.data.reportDetails?.since)
    const showNotificationDot =
        newsSinceDateTime &&
        newsCountResult.data.reportSummary.numberOfNewsArticles > 0 &&
        (!lastNewsCheckDateTime || newsSinceDateTime > lastNewsCheckDateTime)

    return (
        <LocalizedStrict id="user-home-top-bar-news-title" attrs={{ title: true }}>
            <Tooltip title="About Facilitynet" placement="bottom">
                <IconButton onClick={handleClick} size="medium" className={cx(classes.iconButton, className)}>
                    <FontAwesomeSvgIcon icon={faMegaphone} />
                    {showNotificationDot && (
                        <Typography variant="caption" className={classes.circleWrap}>
                            <FontAwesomeIcon icon={faCircle} size="xs" />
                        </Typography>
                    )}
                </IconButton>
            </Tooltip>
        </LocalizedStrict>
    )
}
